import React, { useState, useEffect } from 'react';
import { Bookmark, Folder, Plus, Search, Star, Tag, Trash, Edit, ExternalLink, Grid, List, Filter } from 'lucide-react';
import { api } from '@/services/client';
import { toast } from 'sonner';
import { ConfirmDialog, Modal } from '@components/ui/Modal';
import { PageLayout } from '@/components/layout/PageLayout';

interface Bookmark {
  id: string;
  title: string;
  url: string;
  description: string;
  favicon_url: string;
  folder_id: string;
  tags: string[];
  visit_count: number;
  last_visited_at: string;
  created_at: string;
}

interface BookmarkFolder {
  id: string;
  name: string;
  icon: string;
  color: string;
  parent_id: string | null;
  position: number;
  created_at: string;
}

const BookmarksPage: React.FC = () => {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [folders, setFolders] = useState<BookmarkFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedBookmarkId, setSelectedBookmarkId] = useState<string | null>(null);

  useEffect(() => {
    fetchBookmarks();
    fetchFolders();
  }, []);

  const fetchBookmarks = async () => {
    try {
      const response = await api.get('/bookmarks');
      setBookmarks(response.data.bookmarks || []);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await api.get('/bookmark-folders');
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const createBookmark = async (data: { title: string; url: string; description: string; folder_id?: string; tags: string[] }) => {
    try {
      await api.post('/bookmarks', data);
      fetchBookmarks();
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to create bookmark:', error);
    }
  };

  const createFolder = async (data: { name: string; icon: string; color: string; parent_id?: string }) => {
    try {
      await api.post('/bookmark-folders', data);
      fetchFolders();
      setShowFolderModal(false);
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  const deleteBookmark = async (id: string) => {
    setSelectedBookmarkId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteBookmark = async () => {
    if (!selectedBookmarkId) return;
    toast.promise(
      api.delete(`/bookmarks/${selectedBookmarkId}`).then(() => fetchBookmarks()),
      {
        loading: 'Deleting bookmark...',
        success: 'Bookmark deleted',
        error: 'Failed to delete bookmark',
      }
    );
    setShowDeleteConfirm(false);
    setSelectedBookmarkId(null);
  };

  const getAllTags = () => {
    const tagsSet = new Set<string>();
    bookmarks.forEach(bookmark => {
      bookmark.tags?.forEach(tag => tagsSet.add(tag));
    });
    return Array.from(tagsSet);
  };

  const filteredBookmarks = bookmarks.filter(bookmark => {
    if (selectedFolder && bookmark.folder_id !== selectedFolder) return false;
    if (searchQuery && !bookmark.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !bookmark.url.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (selectedTags.length > 0 && !selectedTags.some(tag => bookmark.tags?.includes(tag))) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <PageLayout title="Bookmarks" description="Organize your bookmarks with folders and tags" icon={Bookmark}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFolderModal(true)}
              className="inline-flex items-center px-4 py-2 border border-khiimori-border/20 rounded-xl text-sm font-medium text-khiimori-text-primary bg-khiimori-bg-elevated/10 hover:bg-khiimori-bg-elevated/20 transition-colors"
            >
              <Folder className="h-4 w-4 mr-2" />
              New Folder
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 shadow-lifted"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Bookmark
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mt-6 flex items-center justify-between">
          <div className="flex-1 flex items-center space-x-4">
            <div className="flex-1 relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-khiimori-text-muted" />
              <input
                type="text"
                placeholder="Search bookmarks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-khiimori-border/20 rounded-xl bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted"
              />
            </div>
            <div className="flex items-center space-x-2">
              {getAllTags().slice(0, 5).map(tag => (
                <button
                  key={tag}
                  onClick={() => {
                    setSelectedTags(prev =>
                      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
                    );
                  }}
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary border border-khiimori-border/20'
                  }`}
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/10'
              }`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/10'
              }`}
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Folders Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl">
            <div className="p-4 border-b border-khiimori-border/10">
              <h3 className="text-sm font-semibold text-khiimori-text-primary">Folders</h3>
            </div>
            <div className="p-2">
              <button
                onClick={() => setSelectedFolder(null)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedFolder === null
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                    : 'text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/10'
                }`}
              >
                <Bookmark className="h-4 w-4" />
                <span>All Bookmarks</span>
                <span className="ml-auto text-xs text-khiimori-text-muted">
                  {bookmarks.length}
                </span>
              </button>
              {folders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => setSelectedFolder(folder.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium mt-1 transition-colors ${
                    selectedFolder === folder.id
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/10'
                  }`}
                >
                  <span>{folder.icon || '📁'}</span>
                  <span>{folder.name}</span>
                  <span className="ml-auto text-xs text-khiimori-text-muted">
                    {bookmarks.filter(b => b.folder_id === folder.id).length}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bookmarks Grid/List */}
        <div className="lg:col-span-3">
          {filteredBookmarks.length === 0 ? (
            <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl p-12 text-center">
              <Bookmark className="mx-auto h-12 w-12 text-khiimori-text-muted" />
              <h3 className="mt-2 text-sm font-medium text-khiimori-text-primary">No bookmarks</h3>
              <p className="mt-1 text-sm text-khiimori-text-muted">
                Get started by creating a new bookmark.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 shadow-lifted"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Bookmark
                </button>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBookmarks.map(bookmark => (
                <div
                  key={bookmark.id}
                  className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl hover:border-khiimori-border/20 hover:shadow-lifted transition-all duration-200 p-4 group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <img
                        src={bookmark.favicon_url || `https://www.google.com/s2/favicons?domain=${new URL(bookmark.url).hostname}`}
                        alt=""
                        className="h-5 w-5 mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-khiimori-text-primary truncate">
                          {bookmark.title}
                        </h4>
                        <p className="text-xs text-khiimori-text-muted mt-1 truncate">
                          {new URL(bookmark.url).hostname}
                        </p>
                      </div>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-1">
                      <a
                        href={bookmark.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-khiimori-text-muted hover:text-primary-400"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => deleteBookmark(bookmark.id)}
                        className="p-1 text-khiimori-text-muted hover:text-error-400"
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {bookmark.description && (
                    <p className="text-xs text-khiimori-text-muted mt-2 line-clamp-2">
                      {bookmark.description}
                    </p>
                  )}
                  {bookmark.tags && bookmark.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {bookmark.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-khiimori-bg-elevated/10 text-khiimori-text-secondary border border-khiimori-border/20"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl divide-y divide-khiimori-border/10">
              {filteredBookmarks.map(bookmark => (
                <div
                  key={bookmark.id}
                  className="p-4 hover:bg-khiimori-bg-elevated/5 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <img
                        src={bookmark.favicon_url || `https://www.google.com/s2/favicons?domain=${new URL(bookmark.url).hostname}`}
                        alt=""
                        className="h-5 w-5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-khiimori-text-primary truncate">
                          {bookmark.title}
                        </h4>
                        <p className="text-xs text-khiimori-text-muted mt-0.5 truncate">
                          {bookmark.url}
                        </p>
                        {bookmark.tags && bookmark.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {bookmark.tags.map(tag => (
                              <span
                                key={tag}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-khiimori-bg-elevated/10 text-khiimori-text-secondary border border-khiimori-border/20"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <a
                        href={bookmark.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-khiimori-text-muted hover:text-primary-400"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button className="p-2 text-khiimori-text-muted hover:text-khiimori-text-primary">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteBookmark(bookmark.id)}
                        className="p-2 text-khiimori-text-muted hover:text-error-400"
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Bookmark Modal */}
      {showAddModal && (
        <div className="fixed inset-0 modal-backdrop flex items-center justify-center z-50">
          <div className="modal-panel rounded-2xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">
              Add Bookmark
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const tags = (formData.get('tags') as string).split(',').map(t => t.trim()).filter(Boolean);
                createBookmark({
                  title: formData.get('title') as string,
                  url: formData.get('url') as string,
                  description: formData.get('description') as string,
                  folder_id: formData.get('folder_id') as string,
                  tags,
                });
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    URL
                  </label>
                  <input
                    type="url"
                    name="url"
                    required
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    rows={2}
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Folder
                  </label>
                  <select
                    name="folder_id"
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary"
                  >
                    <option value="">No folder</option>
                    {folders.map(folder => (
                      <option key={folder.id} value={folder.id}>
                        {folder.icon} {folder.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    placeholder="work, important, read-later"
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted"
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-khiimori-border/20 rounded-lg text-sm font-medium text-khiimori-text-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600"
                >
                  Add Bookmark
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 modal-backdrop flex items-center justify-center z-50">
          <div className="modal-panel rounded-2xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">
              Create Folder
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                createFolder({
                  name: formData.get('name') as string,
                  icon: formData.get('icon') as string,
                  color: formData.get('color') as string,
                });
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Icon (emoji)
                  </label>
                  <input
                    type="text"
                    name="icon"
                    placeholder="📁"
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Color
                  </label>
                  <input
                    type="color"
                    name="color"
                    defaultValue="#3B82F6"
                    className="w-full h-10 px-1 py-1 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10"
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowFolderModal(false)}
                  className="px-4 py-2 border border-khiimori-border/20 rounded-lg text-sm font-medium text-khiimori-text-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600"
                >
                  Create Folder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteBookmark}
        title="Delete Bookmark"
        message="Are you sure you want to delete this bookmark? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </PageLayout>
  );
};

export default BookmarksPage;
